import PropTypes from "prop-types"
import React, { useMemo, useState, useEffect } from "react"
import swal from 'sweetalert'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  UncontrolledTooltip,
  Badge
} from "reactstrap"
import { Link } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import TableContainer from "../../components/Common/TableContainer"
import { delAdmin, getallAdmins } from "../../helpers/requests" // Import the getallAdmins function

import { Oval } from "react-loader-spinner";

const AdminList = (props) => {
  useEffect(() => {
    document.title = "Administrateurs"
  }, [])

  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState(() => localStorage.getItem("adm_filter") || "all");
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("adm_search") || "");

  useEffect(() => {
    localStorage.setItem("adm_filter", filter);
    localStorage.setItem("adm_search", searchTerm);
  }, [filter, searchTerm]);

  // Fetch admins from the backend when the component loads
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const data = await getallAdmins()
        setAdmins(data)
       
      } catch (error) {
        console.error("Error fetching admins:", error)
        swal("Erreur", "Impossible de charger les administrateurs", "error")
      } finally {
        setIsLoading(false)
      }
    }

    fetchAdmins()
  }, [])

  const filteredAdmins = useMemo(() => {
    let filtered = admins;

    if (filter !== "all") {
      filtered = filtered.filter((admin) => admin.role === filter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (admin) =>
          admin.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          admin.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [admins, filter, searchTerm]);

  const handleDelete = (adminData) => {
    swal({
      title: "Êtes-vous sûr?",
      text: "Voulez-vous vraiment supprimer cet administrateur?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
    .then(async(willDelete) => {
      if (willDelete) {
        await delAdmin(adminData.id)
        const data = await getallAdmins()
        setAdmins(data)
        swal("Administrateur supprimé!", {
          icon: "success",
        })
      } else {
        swal("Suppression annulée")
      }
    })
  }

  const columns = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        cell: (cellProps) => <span>{cellProps.row.original.id}</span>,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Nom d\'utilisateur',
        accessorKey: 'username',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Email',
        accessorKey: 'email',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Rôle',
        accessorKey: 'role',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const roleColors = {
            SUPER: "primary",
            MODERATOR: "info",
            OPERATOR: "warning"
          }
          return (
            <Badge color={roleColors[cellProps.row.original.role] || "secondary"}>
              {cellProps.row.original.role}
            </Badge>
          );
        },
      },
      {
        header: 'Date de Création',
        accessorKey: 'created_at',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          return new Date(cellProps.row.original.created_at).toLocaleDateString()
        }
      },
      {
        header: 'Actions',
        accessorKey: 'action',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            
            <Link
              to="#"
              className="text-danger"
              onClick={() => handleDelete(cellProps.row.original)}
            >
              <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
              <UncontrolledTooltip placement="top" target="deletetooltip">
                Supprimer
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    [admins]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Administrateurs")} breadcrumbItem={props.t("Liste des Administrateurs")} />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle style={{ marginBottom: "40px" }}>{props.t("Liste des Administrateurs")}</CardTitle>

                  <Row className="mb-3">
                    <Col md="4">
                      <FormGroup>
                        <Label for="filterSelect">Filtrer par Rôle</Label>
                        <Input
                          type="select"
                          id="filterSelect"
                          value={filter}
                          onChange={(e) => setFilter(e.target.value)}
                        >
                          <option value="all">Tous</option>
                          <option value="SUPER">Super Admin</option>
                          <option value="MODERATOR">Modérateur</option>
                          
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="searchInput">Recherche</Label>
                        <Input
                          type="text"
                          id="searchInput"
                          placeholder="Rechercher par nom ou email"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4" className="d-flex align-items-end justify-content-center mb-2">
                      <Button
                        color="success"
                        className="btn-rounded waves-effect waves-light mb-2"
                        onClick={() => { window.location.href = '/admins/create' }}
                      >
                        Ajouter un Administrateur
                      </Button>
                    </Col>
                  </Row>

                  {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                      <Oval height={80} width={80} color="#556ee6" visible={true} ariaLabel="oval-loading" secondaryColor="#4fa94d" strokeWidth={2} strokeWidthSecondary={2} />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={filteredAdmins}
                      isGlobalFilter={false}
                      isAddButton={false}
                      isPagination={true}
                      SearchPlaceholder="Rechercher un administrateur"
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

AdminList.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(AdminList)